import * as React from "react";
import { signIn, useSession, authClient } from "./lib/auth-client";
import { User, type LoginRequest, type AuthContextType } from "./interfaces";
import type { Group } from "./types";
import { useLocalStorage } from "./utils/useLocalStorage";
import { useUserGroups } from "./services/authentication";

const AuthContext = React.createContext<AuthContextType>(null!);

export const useAuth = () => {
  return React.useContext(AuthContext);
};

const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const { data: sessionData, isPending: sessionPending } = useSession();
  const [selectedGroup, setSelectedGroup] = React.useState<Group | undefined>();
  const [token, setToken] = useLocalStorage("token", null);

  const isAuthenticated = !!sessionData?.user;

  // Only query groups when there is a valid session
  const { data: groups } = useUserGroups(isAuthenticated);
  React.useEffect(() => {
    if (!selectedGroup && groups && Array.isArray(groups) && groups.length > 0) {
      setSelectedGroup(groups[0] as Group);
    }
  }, [groups, selectedGroup]);

  React.useEffect(() => {
    if (sessionData?.user && token) {
      // Token is managed by auth-client.ts now
    }
  }, [sessionData, token]);

  const signin = async (login: LoginRequest, callback?: VoidFunction) => {
    const { data, error } = await signIn.email({
      email: login.email,
      password: login.password,
    });
    if (!error) {
      // @ts-ignore
      const jwtToken = authClient.getJwtToken?.();
      if (jwtToken) {
        setToken(jwtToken);
      }
      callback?.();
      // Return the user data so callers can inspect emailVerified
      return data;
    } else {
      throw error;
    }
  };

  const signout = async (callback?: VoidFunction) => {
    await authClient.signOut();
    setToken(null);
    setSelectedGroup(undefined);
    callback?.();
  };

  const user = sessionData?.user as unknown as User;
  const isAdmin = !!user?.isAdmin || (user?.email ?? '').toLowerCase() === 'dan@farm.red';

  const value = {
    token,
    user,
    isAdmin,
    emailVerified: (sessionData?.user as any)?.emailVerified ?? false,
    signin,
    signout,
    selectedGroup,
    setSelectedGroup,
  };

  if (sessionPending) {
    return <div>Loading session...</div>;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;